import { useEffect, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { environment } from '../environment/environment';
import { isTauri } from '../utils/tauri';

const POLL_INTERVAL = 300_000; // 5 min — desktop app + MCP update checks
// The MCP binary (~99 MB) downloads in the background right after launch, so the
// restart flag flips a few seconds in. Poll it fast so the "Restart Claude" prompt
// appears promptly instead of waiting up to a full 60s poll tick.
const RESTART_POLL_INTERVAL = 3_000;

export interface DesktopUpdateState {
  rustUpdate: string | null;  // a newer signed app build is available (install + relaunch)
  mcpUpdate: boolean;         // the MCP server changed since this session started (relaunch to apply)
  mcpPendingRestart: boolean; // a newer MCP was downloaded this session (restart Claude to load it)
}

/**
 * Polls (desktop only) for app + MCP updates while the app is open — mirroring
 * the web version-check's polling — so a deploy is surfaced within ~a minute.
 * Non-destructive: it only reports; the UI prompts the user to act.
 *
 *  - rustUpdate : a newer signed build exists (check_update) -> install + relaunch
 *  - mcpUpdate  : the public /mcp/manifest.json version changed since startup
 *                 (same one-way-latch approach as the web version check, so it
 *                 clears cleanly on relaunch) -> relaunch downloads the new binary
 */
export function useDesktopUpdates(): DesktopUpdateState {
  const [rustUpdate, setRustUpdate] = useState<string | null>(null);
  const [mcpUpdate, setMcpUpdate] = useState(false);
  const [mcpPendingRestart, setMcpPendingRestart] = useState(false);
  const initialMcp = useRef<string | null>(null);

  useEffect(() => {
    if (!isTauri()) return;
    let active = true;
    const base = environment.skillsBaseUrl.replace(/\/+$/, '');

    const poll = async () => {
      // App build update (signed, via the updater)
      try {
        const v = await invoke<string | null>('check_update', { endpoint: `${base}/updates/latest.json` });
        if (active && v) setRustUpdate(v);
      } catch { /* offline / no update */ }

      // MCP update: compare the current public manifest version to the one seen
      // at startup. One-way latch (like the web check) so it clears on relaunch.
      try {
        const res = await fetch(`${base}/mcp/manifest.json?t=${Date.now()}`);
        if (res.ok) {
          const { version } = await res.json();
          if (initialMcp.current === null) initialMcp.current = version;
          else if (version !== initialMcp.current && active) setMcpUpdate(true);
        }
      } catch { /* offline / no manifest */ }
    };

    poll();
    const timer = setInterval(poll, POLL_INTERVAL);
    return () => { active = false; clearInterval(timer); };
  }, []);

  // A newer MCP binary downloads in the background right after launch; the Rust
  // setup thread flips the restart flag once it lands. Poll it fast (and stop as
  // soon as it's set) so the "Restart Claude" prompt appears within a few seconds
  // of the download finishing, not at the next 60s tick.
  useEffect(() => {
    if (!isTauri()) return;
    let active = true;
    let timer: ReturnType<typeof setInterval>;

    const check = async () => {
      try {
        const pending = await invoke<boolean>('mcp_pending_restart');
        if (active && pending) {
          setMcpPendingRestart(true);
          clearInterval(timer); // flag is one-way; no need to keep polling
        }
      } catch { /* command unavailable */ }
    };

    check();
    timer = setInterval(check, RESTART_POLL_INTERVAL);
    return () => { active = false; clearInterval(timer); };
  }, []);

  return { rustUpdate, mcpUpdate, mcpPendingRestart };
}
